<template>
    <section class="welcome" :style="{ '--clouds-image': `url(${cloudsUrl})` }">
        <header class="welcome__nav anim-fade-down">
            <div class="brand">
                <img :src="logoUrl" alt="Logo Saineo" class="brand__mark" />
                <span class="brand__name">saineo</span>
            </div>
            <nav class="navlinks">
                <router-link to="/partenaires" class="navlinks__item">Professionnels</router-link>
                <router-link to="/faq" class="navlinks__item">FAQ</router-link>
                <router-link to="/contact" class="navlinks__item">Contact</router-link>
                <button class="navlinks__item" @click="$router.push('/login')">Se connecter</button>
            </nav>
            <div class="nav-actions">
                <button class="btn btn--solid" @click="$router.push('/register')">Essai gratuit</button>
            </div>
            <button class="hamburger" :class="{ 'hamburger--open': mobileMenuOpen }" @click="mobileMenuOpen = !mobileMenuOpen" aria-label="Menu">
                <span></span>
                <span></span>
                <span></span>
            </button>
        </header>

        <div class="mobile-menu" :class="{ 'mobile-menu--open': mobileMenuOpen }">
            <router-link to="/partenaires" class="mobile-menu__item" @click="mobileMenuOpen = false">Professionnels</router-link>
            <router-link to="/faq" class="mobile-menu__item" @click="mobileMenuOpen = false">FAQ</router-link>
            <router-link to="/contact" class="mobile-menu__item" @click="mobileMenuOpen = false">Contact</router-link>
            <button class="mobile-menu__item" @click="$router.push('/login'); mobileMenuOpen = false">Se connecter</button>
            <hr class="mobile-menu__divider" />
            <button class="btn btn--solid mobile-menu__cta" @click="$router.push('/register'); mobileMenuOpen = false">Essai gratuit</button>
        </div>

        <div class="welcome__hero">
            <main class="hero">
                <div class="hero__content anim-fade-up" style="--delay: 100ms">
                    <h1 class="hero__title">
                        <span class="hero__title-line">Des menus enfin adaptés</span>
                        <span class="hero__title-line">à ta santé</span>
                    </h1>
                    <p class="hero__subtitle">
                        Génère automatiquement des menus équilibrés qui respectent tes allergies,
                        intolérances et conditions médicales comme l'endométriose.
                    </p>
                    <div class="hero__actions">
                        <button class="btn btn--solid" @click="$router.push('/register')">
                            Générer mon menu
                        </button>
                        <button class="btn btn--ghost" @click="scrollToSteps">
                            Comment ça marche ?
                        </button>
                    </div>
                </div>
                <div class="hero__visual anim-fade-up" style="--delay: 300ms" aria-hidden="true">
                    <div class="hero__card">
                        <img :src="dashboardUrl" alt="Aperçu du tableau de bord Saineo" class="hero__image" loading="lazy" />
                    </div>
                </div>
            </main>
        </div>

        <div class="welcome__body">
            <section class="chronic scroll-reveal" aria-labelledby="chronic-title">
                <div class="chronic__inner">
                    <div class="chronic__media">
                        <img :src="chronicUrl" alt="Préparer un repas sain" class="chronic__image scroll-reveal" />
                    </div>
                    <div class="chronic__content">
                        <span class="chronic__eyebrow">Alimentation &amp; maladies chroniques</span>
                        <h2 id="chronic-title" class="chronic__title">
                            1 Français sur 3<br />
                            vit avec une<br />
                            maladie chronique
                        </h2>
                        <p class="chronic__text">
                            2 milliards d'humains ont une alimentation trop riche, trop sucrée, trop salée, source de
                            maladies. Les maladies chroniques (diabète de type 2, obésité, maladies cardiovasculaires,
                            cancer...) représentent aujourd'hui environ 85% de toutes les maladies et leur fréquence
                            augmente dès 40 ans. La nutrition est aujourd'hui reconnue comme étant l'un des principaux
                            facteurs de risque sur lequel nous pouvons agir, pourtant les produits ultra-transformés
                            poursuivent leur développement à l'horizon 2040.
                        </p>
                        <button class="chronic__cta" @click="$router.push('/register')">
                            Je reprends le contrôle de ma santé
                        </button>
                    </div>
                </div>
            </section>

            <section class="solution scroll-reveal" id="solution" aria-labelledby="solution-title">
                <div class="solution__inner">
                    <span class="solution__eyebrow">La solution</span>
                    <h2 id="solution-title" class="solution__title"><span class="solution__title-mobile">Une nutrition adaptée<br class="mobile-br">à ta santé</span><span class="solution__title-desktop">Une nutrition adaptée à ta santé</span></h2>
                    <div class="solution-card">
                        <h3 class="solution-card__title">Tes conditions médicales prises en compte</h3>
                        <div class="solution-carousel" ref="carouselRef" @mouseenter="onCarouselMouseEnter" @mouseleave="onCarouselMouseLeave" @mousemove="onCarouselMouseMove" @touchstart="onCarouselTouchStart" @touchend="onCarouselTouchEnd">
                            <div class="solution-carousel__hover solution-carousel__hover--left" @mouseenter="carouselDirection = 'left'" @mouseleave="carouselDirection = null"></div>
                            <div class="solution-carousel__hover solution-carousel__hover--right" @mouseenter="carouselDirection = 'right'" @mouseleave="carouselDirection = null"></div>
                            <div class="solution-carousel__track" :style="carouselTrackStyle">
                                <div class="solution-carousel__item"><img :src="solutionImg1" alt="Intolérance au gluten" /></div>
                                <div class="solution-carousel__item"><img :src="solutionImg2" alt="Prévention de l'alzheimer" /></div>
                                <div class="solution-carousel__item"><img :src="solutionImg3" alt="Endométriose" /></div>
                                <div class="solution-carousel__item"><img :src="solutionImg4" alt="Intolérance alimentaire" /></div>
                                <div class="solution-carousel__item"><img :src="solutionImg5" alt="Condition 5" /></div>
                                <div class="solution-carousel__item"><img :src="solutionImg6" alt="Condition 6" /></div>
                                <div class="solution-carousel__item"><img :src="solutionImg1" alt="Intolérance au gluten" /></div>
                                <div class="solution-carousel__item"><img :src="solutionImg2" alt="Prévention de l'alzheimer" /></div>
                                <div class="solution-carousel__item"><img :src="solutionImg3" alt="Endométriose" /></div>
                                <div class="solution-carousel__item"><img :src="solutionImg4" alt="Intolérance alimentaire" /></div>
                                <div class="solution-carousel__item"><img :src="solutionImg5" alt="Condition 5" /></div>
                                <div class="solution-carousel__item"><img :src="solutionImg6" alt="Condition 6" /></div>
                            </div>
                        </div>
                        <p class="solution-card__text">
                            Saineo ne génère pas de menus au hasard, il développe son
                            propre moteur nutritionnel basé sur des règles scientifiques définies par des diététiciens
                            qui calculent précisément chaque proportion selon ton profil.
                        </p>
                    </div>
                    <div class="solution__bottom">
                        <div class="solution-mini">
                            <h3>Mais aussi tes objectifs</h3>
                            <p>Prise de masse, perte de poids, maintien...</p>
                        </div>
                        <div class="solution-mini">
                            <h3>Et ton régime alimentaire</h3>
                            <p>Végétarien, végétalien, flexitarien...</p>
                        </div>
                    </div>
                </div>
            </section>

            <section class="steps scroll-reveal" id="steps-section" aria-labelledby="steps-title">
                <div class="steps__inner">
                    <span class="steps__eyebrow">
                        0 charge mentale, une meilleure santé, un budget maîtrisé, aucun gaspillage alimentaire
                    </span>
                    <h2 id="steps-title" class="steps__title">Comment ça marche ?</h2>
                    <div class="steps__grid">
                        <article class="steps-card scroll-reveal" style="transition-delay: 0ms">
                            <img :src="img1profileUrl" alt="Configurer ton profil" class="steps-card__image" />
                            <h3 class="steps-card__title">1. Je configure mon profil</h3>
                            <p class="steps-card__text">
                                Je renseigne mon profil (âge, poids, grossesse en cours), mes contraintes médicales
                                (allergies, intolérances, pathologies) et mes objectifs (prise de masse, maintien du poids, perte de poids).
                            </p>
                        </article>
                        <article class="steps-card scroll-reveal" style="transition-delay: 150ms">
                            <img :src="img2menuUrl" alt="Générer ton menu" class="steps-card__image" />
                            <h3 class="steps-card__title">2. Je récupère mon menu sur 7 jours</h3>
                            <p class="steps-card__text">
                                Saineo génère mon menu de la semaine, personnalisé, sans produits ultra-transformés
                                et validé par les nutritionnistes. Je peux également intégrer mes propres recettes qui seront
                                scannées par Saineo en temps réel avec un score santé.
                            </p>
                        </article>
                        <article class="steps-card scroll-reveal" style="transition-delay: 300ms">
                            <img :src="img3coursesUrl" alt="Liste de courses" class="steps-card__image" />
                            <h3 class="steps-card__title">3. Je fais mes courses</h3>
                            <p class="steps-card__text">
                                Saineo génère ma liste de courses pour la semaine. Les ingrédients sont regroupés par catégories,
                                en quantités exactes, 0 gâchis. Je peux ajouter d'autres produits, par exemple ménagers, pour ne rien oublier.
                            </p>
                        </article>
                        <article class="steps-card scroll-reveal" style="transition-delay: 450ms">
                            <img :src="img4cookUrl" alt="Cuisine chaque soir" class="steps-card__image" />
                            <h3 class="steps-card__title">4. Je cuisine l'esprit tranquille</h3>
                            <p class="steps-card__text">
                                Je n'ai plus qu'à suivre mon menu chaque jour ou préparer mes recettes à l'avance. 
                                Je rentre chez moi sereinement, en sachant exactement quoi manger et que mes repas sont bons pour ma santé.
                            </p>
                        </article>
                    </div>
                </div>
            </section>

            <section class="platforms scroll-reveal" aria-labelledby="platforms-title">
                <div class="platforms__inner">
                    <span class="platforms__eyebrow">Multi-plateformes</span>
                    <h2 id="platforms-title" class="platforms__title">
                        Saineo est disponible sur ton ordi<br />
                        et ton téléphone
                    </h2>
                    <div class="platforms__card">
                        <img :src="activePlatform === 'mobile' ? phoneMockupUrl : webMockupUrl" alt="Aperçu Saineo" class="platforms__image" />
                        <div class="platforms__switch">
                            <span class="platforms__pill" :class="{ 'platforms__pill--active': activePlatform === 'web' }" @click="activePlatform = 'web'" style="cursor:pointer">Web</span>
                            <span class="platforms__pill" :class="{ 'platforms__pill--active': activePlatform === 'mobile' }" @click="activePlatform = 'mobile'" style="cursor:pointer">Mobile</span>
                        </div>
                    </div>
                </div>
            </section>

            <section class="testimonial scroll-reveal" aria-labelledby="testimonial-title">
                <div class="testimonial__inner">
                    <h2 id="testimonial-title" class="testimonial__title">Certifié par les nutritionnistes</h2>
                    <p class="testimonial__quote">
                        "Diététicienne passionnée et engagée, j'accompagne chacun vers une alimentation équilibrée,
                        simple et durable. Avec Saineo, j'ai souhaité mettre mon expertise au service d'un outil
                        enfin fiable et humain. J'allie expertise théorique et approche bienveillante pour créer et
                        valider des contenus fiables, accessibles et adaptés au quotidien."
                    </p>
                    <div class="testimonial__person">
                        <div class="testimonial__avatar" :style="{ '--testimonial-avatar': `url(${testimonialUrl})` }" role="img" aria-label="Portrait de Rachel Roux"></div>
                        <div class="testimonial__identity">
                            <h3 class="testimonial__name">Rachel Roux</h3>
                            <p class="testimonial__role">Diététicienne-Nutritionniste</p>
                        </div>
                    </div>
                </div>
            </section>

            <section class="pricing scroll-reveal" id="pricing" aria-labelledby="pricing-title">
                <div class="pricing__inner">
                    <span class="pricing__badge">MON PREMIER MENU OFFERT</span>
                    <h2 id="pricing-title" class="pricing__title">Je choisis mon abonnement</h2>
                    <div class="pricing__grid">
                        <article class="pricing-card scroll-reveal" style="transition-delay: 0ms">
                            <div class="pricing-card__head">
                                <p class="pricing-card__plan-name">Saineo Essentiel</p>
                                <p class="pricing-card__price">7.99 €/mois</p>
                                <p class="pricing-card__subtitle">L'essentiel pour démarrer.</p>
                            </div>
                            <ul class="pricing-card__list">
                                <li>Recettes saines &amp; adaptées</li>
                                <li>Génération du menu</li>
                                <li>Liste de courses</li>
                                <li>Accès aux recettes de base</li>
                                <li>Support par email</li>
                            </ul>
                            <button class="pricing-card__cta" @click="$router.push('/register')">
                                Commencer
                            </button>
                        </article>
                        <article class="pricing-card pricing-card--highlight scroll-reveal" style="transition-delay: 150ms">
                            <div class="pricing-card__toggle">
                                <span class="pricing-card__tab" :class="{ 'pricing-card__tab--active': proplan === 'monthly' }" @click="proplan = 'monthly'" style="cursor:pointer">Mensuel</span>
                                <span class="pricing-card__tab" :class="{ 'pricing-card__tab--active': proplan === 'annual' }" @click="proplan = 'annual'" style="cursor:pointer">Annuel</span>
                            </div>
                            <div class="pricing-card__head">
                                <div class="pricing-card__plan">
                                    <span>Saineo Pro</span>
                                    <span v-if="proplan === 'annual'" class="pricing-card__pill">Économise 20%</span>
                                </div>
                                <p class="pricing-card__price">
                                    <span v-if="proplan === 'monthly'">13.99 €/mois</span>
                                    <span v-else>134 €/an</span>
                                </p>
                                <p class="pricing-card__subtitle">
                                    <span v-if="proplan === 'monthly'">Mon menu, chaque semaine.</span>
                                    <span v-else>Soit 11.17 €/mois, 2 mois offerts.</span>
                                </p>
                            </div>
                            <ul class="pricing-card__list">
                                <li>Recettes saines &amp; adaptées</li>
                                <li>Scan de produits ultra-transformés</li>
                                <li>Génération du menu</li>
                                <li>Validation par les nutritionnistes</li>
                                <li>Liste de courses</li>
                            </ul>
                            <button class="pricing-card__cta pricing-card__cta--dark" @click="$router.push('/register')">
                                Essayer gratuitement
                            </button>
                        </article>
                        <article class="pricing-card scroll-reveal" style="transition-delay: 300ms">
                            <div class="pricing-card__head">
                                <p class="pricing-card__plan-name">Saineo Premium</p>
                                <p class="pricing-card__price">64.99 €/mois</p>
                                <p class="pricing-card__subtitle">Menu hebdomadaire avec suivi par un nutritionniste.</p>
                            </div>
                            <ul class="pricing-card__list">
                                <li>Fonctionnalités Saineo Pro</li>
                                <li>Entretien visio au démarrage</li>
                                <li>Accompagnement par un nutritionniste</li>
                                <li>Disponible en temps réel</li>
                                <li>Conseils personnalisés</li>
                            </ul>
                            <button class="pricing-card__cta" @click="$router.push('/register')">
                                Commencer mon suivi
                            </button>
                        </article>
                    </div>
                </div>
            </section>

            <section class="cta-final scroll-reveal" aria-labelledby="cta-final-title">
                <div class="cta-final__inner">
                    <h2 id="cta-final-title" class="cta-final__title">
                        Je renseigne mon profil<br />Saineo fait le reste
                    </h2>
                    <p class="cta-final__subtitle">
                        Rejoins des milliers d'utilisateurs qui ont repris le contrôle de leur santé.
                    </p>
                    <button class="cta-final__button" @click="$router.push('/register')">
                        Essayer gratuitement
                    </button>
                </div>
            </section>
        </div>
    <button class="scroll-top" :class="{ 'scroll-top--visible': showScrollTop }" @click="scrollToTop" aria-label="Remonter en haut">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="18 15 12 9 6 15"></polyline>
        </svg>
    </button>
    </section>
</template>

<script>
    import cloudsUrl from '@/../assets/clouds.png';
    import logoUrl from '@/../assets/logo.png';
    import testimonialUrl from '@/../assets/testimonialPicture.png';
    import phoneMockupUrl from '@/../assets/phoneMockup.png';
    import webMockupUrl from '@/../assets/webMockup.png';
    import chronicUrl from '@/../assets/chronicPicture.jpg';
    import img1profileUrl from '@/../assets/1profile.png';
    import img2menuUrl from '@/../assets/2menu.png';
    import img3coursesUrl from '@/../assets/3courses.png';
    import img4cookUrl from '@/../assets/4cook.png';
    
    import solutionImg1 from '@/../assets/1.png';
    import solutionImg2 from '@/../assets/2.png';
    import solutionImg3 from '@/../assets/3.png';
    import solutionImg4 from '@/../assets/4.png';
    import solutionImg5 from '@/../assets/5.png';
    import solutionImg6 from '@/../assets/6.png';
    import dashboardUrl from '@/../assets/dashboard.png';

    import { ref, computed } from 'vue';

    export default {
        name: 'WelcomePage',
        setup() {
            const mobileMenuOpen = ref(false);
            const showScrollTop = ref(false);

            const scrollToTop = () => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            };

            window.addEventListener('scroll', () => {
                showScrollTop.value = window.scrollY > 400;
            }, { passive: true });
            const activePlatform = ref('web');
            const proplan = ref('monthly');

            const scrollToConstraints = () => {
                const target = document.getElementById('solution');
                if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            };

            // Carousel
            const carouselRef = ref(null);
            const carouselPaused = ref(false);
            const carouselDirection = ref(null);
            const carouselSpeed = ref(30);

            const carouselTrackStyle = computed(() => ({
                animationDuration: carouselSpeed.value + 's',
                animationPlayState: carouselPaused.value ? 'paused' : 'running',
                animationDirection: carouselDirection.value === 'left' ? 'reverse' : 'normal',
            }));

            const onCarouselMouseEnter = () => { carouselPaused.value = false; };
            const onCarouselMouseLeave = () => { carouselPaused.value = false; carouselDirection.value = null; carouselSpeed.value = 30; };
            const onCarouselMouseMove = (e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const w = rect.width;
                const zone = 0.2; // 20% on each side
                if (x < w * zone) {
                    carouselDirection.value = 'left';
                    carouselSpeed.value = 18;
                    carouselPaused.value = false;
                } else if (x > w * (1 - zone)) {
                    carouselDirection.value = 'right';
                    carouselSpeed.value = 18;
                    carouselPaused.value = false;
                } else {
                    carouselDirection.value = null;
                    carouselSpeed.value = 30;
                    carouselPaused.value = true;
                }
            };

            // Mobile touch: tap pauses, second tap resumes
            let carouselTouchPaused = false;
            const onCarouselTouchStart = () => {
                carouselTouchPaused = !carouselTouchPaused;
                carouselPaused.value = carouselTouchPaused;
            };
            const onCarouselTouchEnd = () => {};

            const scrollToSteps = () => {
                const target = document.getElementById('steps-section');
                if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            };

            const scrollToPricing = () => {
                const target = document.getElementById('pricing');
                if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            };

            const handleNavScroll = () => {
                const header = document.querySelector('.welcome__nav');
                if (!header) return;
                if (window.scrollY > 40) {
                    header.classList.add('welcome__nav--scrolled');
                } else {
                    header.classList.remove('welcome__nav--scrolled');
                }
            };
            window.addEventListener('scroll', handleNavScroll, { passive: true });

            const initScrollReveal = () => {
                const observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            entry.target.classList.add('scroll-reveal--visible');
                            observer.unobserve(entry.target);
                        }
                    });
                }, { threshold: 0.06, rootMargin: "0px 0px -60px 0px" });
                document.querySelectorAll('.scroll-reveal').forEach(el => observer.observe(el));
            };

            const initPerspectiveScroll = () => {
                const card = document.querySelector('.hero__card');
                if (!card) return;
                const updatePerspective = () => {
                    const rect = card.getBoundingClientRect();
                    const windowH = window.innerHeight;
                    // Flat when card center reaches screen center
                    const cardCenter = rect.top + rect.height / 2;
                    const screenCenter = windowH / 2;
                    const distFromCenter = cardCenter - screenCenter;
                    const maxDist = windowH;
                    const progress = Math.min(1, Math.max(0, 1 - (distFromCenter / maxDist)));
                    const rotateX = 32 * (1 - progress);
                    const scale = 0.82 + 0.18 * progress;
                    card.style.transform = `perspective(900px) rotateX(${rotateX}deg) scale(${scale})`;
                };
                window.addEventListener('scroll', updatePerspective, { passive: true });
                updatePerspective();
            };

            setTimeout(() => {
                initScrollReveal();
                initPerspectiveScroll();
            }, 100);

            return {
                mobileMenuOpen,
                cloudsUrl, logoUrl, testimonialUrl, phoneMockupUrl, chronicUrl,
                img1profileUrl, img2menuUrl, img3coursesUrl, img4cookUrl,
                solutionImg1, solutionImg2, solutionImg3,
                solutionImg4, solutionImg5, solutionImg6,
                dashboardUrl, scrollToConstraints, scrollToPricing, scrollToSteps,
                activePlatform, webMockupUrl, proplan, showScrollTop, scrollToTop,
                carouselRef, carouselPaused, carouselDirection, carouselTrackStyle,
                onCarouselMouseEnter, onCarouselMouseLeave, onCarouselMouseMove,
                onCarouselTouchStart, onCarouselTouchEnd,
            };
        },
    }
</script>

<style scoped>
    /* ===== VARIABLES & BASE ===== */
    .welcome {
        --welcome-ink: #1A1615;
        --welcome-muted: #4c5562;
        --welcome-accent: #1A1615;
        --welcome-radius: 999px;
        --welcome-inline-pad: clamp(20px, 6vw, 80px);
        min-height: 100vh;
        padding: 0;
        background: transparent;
        color: var(--welcome-ink);
        font-family: "Open Runde", "Montserrat", "Segoe UI", sans-serif;
        /* PAS de transform ici — ça casserait le position:fixed de la nav */
        position: relative;
    }

    @font-face {
        font-family: "Open Runde";
        src: url("../../../assets/OpenRunde-Regular.otf") format("opentype");
        font-weight: 400; font-style: normal; font-display: swap;
    }
    @font-face {
        font-family: "Open Runde";
        src: url("../../../assets/OpenRunde-Medium.otf") format("opentype");
        font-weight: 500; font-style: normal; font-display: swap;
    }
    @font-face {
        font-family: "Open Runde";
        src: url("../../../assets/OpenRunde-Semibold.otf") format("opentype");
        font-weight: 600; font-style: normal; font-display: swap;
    }
    @font-face {
        font-family: "Open Runde";
        src: url("../../../assets/OpenRunde-Bold.otf") format("opentype");
        font-weight: 700; font-style: normal; font-display: swap;
    }

    /* ===== STICKY NAV ===== */
    /*
     * FIX CENTRAGE :
     * On utilise margin-inline:auto + left:0/right:0 au lieu de left:50%+translateX
     * car translateX interfère avec les animations et certains contextes de stacking.
     * position:fixed se base sur le viewport → width fixe + margin auto = centrage garanti.
     */
    .welcome__nav {
        position: fixed;
        top: 24px;
        left: 0;
        right: 0;
        width: calc(100% - 80px);
        max-width: 960px;
        margin: 0 auto;
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 24px;
        padding: 0;
        border-radius: 0;
        transition: background 0.4s ease, box-shadow 0.4s ease,
                    padding 0.4s ease, border-radius 0.4s ease;
    }

    .welcome__nav--scrolled {
        background: rgba(240, 238, 235, 0.88);
        backdrop-filter: blur(16px);
        -webkit-backdrop-filter: blur(16px);
        box-shadow: 0 4px 24px rgba(20, 28, 40, 0.10);
        border-radius: 999px;
        padding: 8px 20px;
        border: 1px solid rgba(255, 255, 255, 0.5);
    }

    /* ===== MOBILE MENU (dropdown sous la nav) ===== */
    .mobile-menu {
        display: none;
        position: fixed;
        top: 72px;
        left: 0;
        right: 0;
        width: calc(100% - 40px);
        max-width: 500px;
        margin-inline: auto;
        flex-direction: column;
        gap: 4px;
        background: rgba(255, 255, 255, 0.97);
        backdrop-filter: blur(14px);
        border-radius: 20px;
        padding: 16px;
        box-shadow: 0 8px 32px rgba(20, 28, 40, 0.14);
        border: 1px solid rgba(255, 255, 255, 0.6);
        z-index: 999;
    }

    .mobile-menu--open {
        display: flex;
    }

    .mobile-menu__item {
        background: none;
        border: none;
        text-align: left;
        padding: 12px 8px;
        font-size: 1rem;
        font-weight: 500;
        color: #1A1615;
        cursor: pointer;
        border-radius: 10px;
        transition: background 0.2s;
        text-decoration: none;
        display: block;
        font-family: inherit;
    }

    .mobile-menu__item:hover { background: rgba(0,0,0,0.05); }

    .mobile-menu__divider {
        border: none;
        border-top: 1px solid rgba(0,0,0,0.08);
        margin: 4px 0;
    }

    .mobile-menu__cta { width: 100%; margin-top: 4px; }

    /* ===== HAMBURGER ===== */
    .hamburger {
        display: none;
        flex-direction: column;
        gap: 5px;
        background: none;
        border: none;
        cursor: pointer;
        padding: 6px;
        flex-shrink: 0;
    }

    .hamburger span {
        display: block;
        width: 22px;
        height: 2px;
        background: #1A1615;
        border-radius: 2px;
        transition: transform 0.3s ease, opacity 0.3s ease;
    }

    .hamburger--open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
    .hamburger--open span:nth-child(2) { opacity: 0; }
    .hamburger--open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

    /* ===== BRAND ===== */
    .brand {
        display: flex;
        align-items: center;
        gap: 10px;
        font-weight: 600;
        letter-spacing: -0.02em;
        flex-shrink: 0;
    }

    .brand__mark { width: 38px; height: 38px; object-fit: contain; display: block; }
    .brand__name { font-size: 20px; text-transform: lowercase; }

    /* ===== NAV LINKS ===== */
    .navlinks {
        display: flex;
        gap: 24px;
        font-weight: 500;
        font-size: 0.95rem;
    }

    .navlinks__item {
        color: #1A1615;
        text-decoration: none;
        opacity: 1;
        font-size: 16px;
        font-weight: 400;
        font-family: "Open Runde", "Montserrat", "Segoe UI", sans-serif;
        transition: opacity 0.2s ease;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 0;
    }

    .navlinks__item:hover { opacity: 0.5; }

    .nav-actions {
        display: flex;
        gap: 16px;
        align-items: center;
        flex-shrink: 0;
    }

    .nav-actions__link {
        background: transparent;
        border: none;
        color: #1A1615;
        font-size: 16px;
        font-weight: 400;
        font-family: "Open Runde", "Montserrat", "Segoe UI", sans-serif;
        cursor: pointer;
        padding: 8px 4px;
        opacity: 1;
        transition: opacity 0.2s ease;
    }

    .nav-actions__link:hover { opacity: 0.5; }

    /* ===== BUTTONS ===== */
    .btn {
        border: none;
        border-radius: var(--welcome-radius);
        padding: 12px 26px;
        font-size: 0.95rem;
        font-weight: 600;
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-family: inherit;
    }

    .btn--ghost {
        background: rgba(255, 255, 255, 0.7);
        color: var(--welcome-ink);
        box-shadow: inset 0 0 0 1px rgba(17, 17, 17, 0.12);
    }

    .btn--solid { background: var(--welcome-accent); color: #ffffff; }
    .btn--solid:hover { transform: translateY(-2px) !important; opacity: 0.85 !important; }
    .btn--ghost:hover { transform: translateY(-2px) !important; opacity: 0.85 !important; }
    .btn:hover {
        transform: translateY(-2px) !important;
        opacity: 0.85 !important;
    }

    /* ===== HERO ===== */
    .welcome__hero {
        padding: 120px var(--welcome-inline-pad) clamp(48px, 8vw, 96px);
        background: linear-gradient(180deg, #9CC1E7 0%, #EDDFD0 100%);
        position: relative;
        z-index: 1;
        overflow: hidden;
        height: 1020px;
        box-sizing: border-box;
    }

    .welcome__hero::before {
        content: "";
        position: absolute;
        inset: 0;
        background-image: var(--clouds-image);
        background-repeat: repeat-x;
        background-position: center 120px;
        background-size: 1400px auto;
        opacity: 0.95;
        pointer-events: none;
        z-index: 0;
    }

    .welcome__hero::after {
        display: none;
    }

    .welcome__hero > * { position: relative; z-index: 1; }

    .hero {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
        width: 100%;
        max-width: 1240px;
        margin-inline: auto;
        padding-bottom: clamp(24px, 6vw, 64px);
    }

    .hero__content {
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 18px;
    }

    .hero__title {
        font-size: clamp(2.2rem, 5.5vw, 68px);
        font-weight: 600;
        letter-spacing: -0.03em;
        margin: 0;
        margin-top: 48px;
        line-height: 1.15;
        text-align: center;
        width: fit-content;
    }

    .hero__title-line { display: block; white-space: nowrap; text-align: center; width: 100%; }

    .hero__subtitle {
        margin: 0;
        font-size: 20px;
        line-height: 1.7;
        color: #453F3D;
        max-width: 680px;
    }

    .hero__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        justify-content: center;
        margin-top: 10px;
    }

    .hero__visual {
        display: flex;
        justify-content: center;
        margin-top: clamp(28px, 6vw, 54px);
        width: 100%;
    }

    .hero__card {
        width: 900px;
        max-width: 94vw;
        margin-inline: auto;
        transform-origin: top center;
        will-change: transform;
    }

    .hero__image {
        width: 100%;
        height: auto;
        object-fit: contain;
        display: block;
        border-radius: 20px;
        box-shadow: 0 40px 80px rgba(20, 28, 40, 0.22);
    }

    /* ===== BODY (gradient wrapper) ===== */
    .welcome__body {
        background: linear-gradient(180deg, #ffffff 0%, #C3DBF3 100%);
        position: relative;
    }

    /* ===== CHRONIC ===== */
    .chronic {
        padding: clamp(64px, 10vw, 120px) var(--welcome-inline-pad);
    }

    .chronic__inner {
        max-width: 1150px;
        margin-inline: auto;
        display: grid;
        grid-template-columns: minmax(280px, 1fr) minmax(320px, 1fr);
        gap: clamp(28px, 5vw, 56px);
        align-items: center;
    }

    .chronic__media {
        border-radius: 28px;
        padding: 18px;
        background: linear-gradient(180deg, rgba(152, 190, 230, 0.6) 0%, rgba(244, 226, 205, 0.7) 100%);
        box-shadow: 0 24px 48px rgba(20, 30, 45, 0.18);
    }

    .chronic__image { width: 100%; height: 615px; display: block; border-radius: 20px; object-fit: cover; }

    .chronic__content { display: flex; flex-direction: column; gap: 16px; }

    .chronic__eyebrow { font-size: 15px; letter-spacing: 0em; line-height: 125%; text-transform: uppercase; font-weight: 600; color: #614A44; }

    .chronic__title { margin: 0; font-size: 52px; font-weight: 600; color: #1A1615; line-height: 120%; letter-spacing: -0.03em; }
    .chronic__text { margin: 0; font-size: 1rem; line-height: 1.65; color: #3a424c; }

    .chronic__cta {
        align-self: flex-start;
        margin-top: 10px;
        padding: 12px 22px;
        border-radius: 999px;
        border: none;
        background: #1A1615;
        color: #ffffff;
        font-weight: 600;
        cursor: pointer;
        font-family: inherit;
        transition: transform 0.2s ease, opacity 0.2s ease;
    }

    .chronic__cta:hover { transform: translateY(-2px) !important; opacity: 0.85 !important; }

    /* ===== SOLUTION ===== */
    .solution { padding: clamp(72px, 12vw, 140px) var(--welcome-inline-pad); }

    .solution__inner {
        max-width: 1120px;
        margin-inline: auto;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 18px;
    }

    .solution__title-mobile { display: none; }
    .solution__title-desktop { display: inline; }

    .solution__eyebrow { font-size: 15px; letter-spacing: 0em; line-height: 125%; text-transform: uppercase; font-weight: 600; color: #614A44; }

    .solution__title { margin: 0 0 40px; font-size: 52px; font-weight: 600; color: #1A1615; line-height: 120%; letter-spacing: -0.03em; }

    .solution-card {
        margin-top: 18px;
        background: rgba(255, 255, 255, 0.85);
        border-radius: 28px;
        padding: 28px;
        width: 100%;
        height: auto;
        text-align: left;
        box-shadow: none;
        box-sizing: border-box;
    }

    .solution-card__title { margin: 0 0 52px; font-size: 28px; font-weight: 600; color: #1A1615; line-height: 140%; letter-spacing: -0.03em; }

    .solution-carousel {
        width: 100%;
        overflow: hidden;
        margin-bottom: 48px;
        mask-image: linear-gradient(90deg, transparent, black 8%, black 92%, transparent);
        -webkit-mask-image: linear-gradient(90deg, transparent, black 8%, black 92%, transparent);
    }

    .solution-carousel__track {
        display: flex;
        gap: 32px;
        animation: carouselScroll 30s linear infinite;
        width: max-content;
    }

    .solution-carousel__hover {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 20%;
        z-index: 2;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .solution-carousel__hover--left { left: 0; }
    .solution-carousel__hover--right { right: 0; }

    .solution-carousel__hover::after {
        content: '';
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: rgba(255,255,255,0.85);
        backdrop-filter: blur(6px);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.2s ease;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231A1615' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='15 18 9 12 15 6'%3E%3C/polyline%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center;
        background-size: 20px;
    }

    .solution-carousel__hover--right::after {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231A1615' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='9 18 15 12 9 6'%3E%3C/polyline%3E%3C/svg%3E");
    }

    .solution-carousel:hover .solution-carousel__hover::after { opacity: 1; }

    .solution-carousel {
        position: relative;
    }

    .solution-carousel__item {
        flex-shrink: 0;
        width: 250px;
        height: 350px;
        border-radius: 10px;
        overflow: hidden;
    }

    .solution-carousel__item img { width: 100%; height: 100%; object-fit: cover; display: block; }

    @keyframes carouselScroll {
        0%   { transform: translateX(0); }
        100% { transform: translateX(calc(-50% - 7px)); }
    }

    /* hover pause handled via JS */

    .solution-card__text { margin: 0; font-size: 18px; line-height: 1.6; color: #453F3D; }

    .solution__bottom {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 18px;
        margin-top: 18px;
    }

    .solution-mini {
        background: rgba(255, 255, 255, 0.85);
        border-radius: 20px;
        padding: 18px 20px;
        text-align: left;
        box-shadow: none;
        height: auto;
        box-sizing: border-box;
    }

    .solution-mini h3 { margin: 0 0 32px; font-size: 28px; font-weight: 600; color: #1A1615; line-height: 140%; letter-spacing: -0.03em; }
    .solution-mini p { margin: 0; color: #453F3D; font-size: 18px; line-height: 1.6; }

    /* ===== STEPS ===== */
    .steps { padding: clamp(72px, 12vw, 140px) var(--welcome-inline-pad); }

    .steps__inner {
        max-width: 1020px;
        margin-inline: auto;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 18px;
    }

    .steps__eyebrow { font-size: 15px; letter-spacing: 0em; line-height: 125%; text-transform: uppercase; font-weight: 600; color: #614A44; }

    .steps__title { margin: 0 0 40px; font-size: 52px; font-weight: 600; color: #1A1615; line-height: 120%; letter-spacing: -0.03em; }

    .steps__grid {
        margin-top: 22px;
        display: grid;
        grid-template-columns: repeat(2, 540px);
        gap: 20px;
        width: 100%;
        justify-content: center;
    }

    .steps-card {
        background: rgba(255, 255, 255, 0.85);
        border-radius: 26px;
        padding: 20px 20px 16px;
        box-shadow: none;
        display: flex;
        flex-direction: column;
        gap: 10px;
        text-align: left;
        height: auto;
        min-height: 400px;
        width: 540px;
        max-width: 100%;
        box-sizing: border-box;
        flex-shrink: 0;
    }

    .steps-card__image { width: 100%; height: 240px; object-fit: cover; border-radius: 18px; display: block; margin-bottom: 24px; }
    .steps-card__title { margin: 0; font-size: 28px; font-weight: 600; color: #1A1615; line-height: 140%; letter-spacing: -0.03em; }
    .steps-card__text { margin: 0 0 8px; color: #453F3D; font-size: 18px; line-height: 1.6; font-weight: 400; }

    /* ===== PLATFORMS ===== */
    .platforms { padding: clamp(72px, 12vw, 140px) var(--welcome-inline-pad); }

    .platforms__inner {
        max-width: 1020px;
        margin-inline: auto;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 18px;
    }

    .platforms__eyebrow { font-size: 15px; letter-spacing: 0em; line-height: 125%; text-transform: uppercase; font-weight: 600; color: #614A44; }

    .platforms__title { margin: 0 0 40px; font-size: 52px; font-weight: 600; color: #1A1615; line-height: 120%; letter-spacing: -0.03em; }

    .platforms__card {
        margin-top: 26px;
        width: min(980px, 92vw);
        border-radius: 28px;
        overflow: hidden;
        box-shadow: 0 30px 60px rgba(20, 28, 40, 0.18);
        position: relative;
    }

    .platforms__image { width: 100%; height: auto; display: block; object-fit: cover; }

    .platforms__switch {
        position: absolute;
        left: 50%;
        bottom: 16px;
        transform: translateX(-50%);
        display: inline-flex;
        gap: 6px;
        background: rgba(0, 0, 0, 0.4);
        border-radius: 999px;
        padding: 5px;
        backdrop-filter: blur(6px);
        white-space: nowrap;
        z-index: 2;
    }

    .platforms__pill { color: #fff; font-size: clamp(0.75rem, 1.5vw, 0.9rem); font-weight: 600; padding: 5px clamp(10px, 2vw, 16px); border-radius: 999px; background: rgba(255,255,255,0.2); }
    .platforms__pill--active { background: #ffffff; color: #1A1615; }

    /* ===== TESTIMONIAL ===== */
    .testimonial { padding: clamp(80px, 12vw, 140px) var(--welcome-inline-pad) clamp(100px, 14vw, 160px); }

    .testimonial__inner {
        max-width: 860px;
        margin-inline: auto;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 18px;
    }

    .testimonial__title { margin: 0 0 40px; font-size: 52px; font-weight: 600; color: #1A1615; line-height: 120%; letter-spacing: -0.03em; }

    .testimonial__quote { margin: 0; font-size: 20px; line-height: 1.75; color: #453F3D; max-width: 920px; }

    .testimonial__avatar {
        width: 60px; height: 60px;
        border-radius: 50%;
        background-image: var(--testimonial-avatar);
        background-size: cover;
        background-position: center;
        box-shadow: 0 10px 20px rgba(20, 30, 45, 0.16);
        border: 3px solid #ffffff;
    }

    .testimonial__person { margin-top: 6px; display: flex; flex-direction: column; align-items: center; gap: 12px; }
    .testimonial__identity { display: flex; flex-direction: column; gap: 2px; align-items: center; }
    .testimonial__name { margin: 0; font-size: 14px; font-weight: 600; color: #757170; }
    .testimonial__role { margin: 0; font-size: 16px; color: #1A1615; }

    /* ===== PRICING ===== */
    .pricing { padding: clamp(72px, 12vw, 140px) var(--welcome-inline-pad); }

    .pricing__inner {
        max-width: 940px;
        margin-inline: auto;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 24px;
    }

    .pricing__badge { font-size: 15px; letter-spacing: 0em; line-height: 125%; text-transform: uppercase; font-weight: 600; color: #614A44; }
    .pricing__title { margin: 0; font-size: 52px; font-weight: 600; color: #1A1615; line-height: 120%; letter-spacing: -0.03em; }

    .pricing__grid {
        margin-top: 20px;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 24px;
        width: 100%;
        align-items: end;
        justify-items: center;
    }

    .pricing-card {
        background: rgba(255, 255, 255, 0.85);
        border-radius: 26px;
        padding: 28px 26px 30px;
        box-shadow: 0 24px 50px rgba(20, 30, 45, 0.14);
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 18px;
        text-align: left;
        max-width: 360px;
        width: 100%;
    }

    .pricing-card--highlight {
        background: linear-gradient(180deg, rgba(129, 178, 228, 0.25) 0%, rgba(255, 255, 255, 0.95) 55%);
        border: 3px solid rgba(125, 170, 220, 0.9);
        box-shadow: 0 28px 60px rgba(20, 40, 60, 0.2);
    }

    .pricing-card__toggle {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 6px;
        background: rgba(255, 255, 255, 0.85);
        border-radius: 999px;
        padding: 6px;
        width: 100%;
        max-width: 250px;
        box-shadow: inset 0 0 0 1px rgba(18, 20, 24, 0.08);
    }

    .pricing-card__tab { text-align: center; padding: 6px 12px; border-radius: 999px; font-weight: 600; font-size: 0.9rem; color: #4b5563; }
    .pricing-card__tab--active { background: #ffffff; color: #1A1615; box-shadow: 0 10px 18px rgba(20, 30, 45, 0.15); }

    .pricing-card__head { display: flex; flex-direction: column; gap: 10px; }
    .pricing-card__plan { display: flex; align-items: center; gap: 10px; font-weight: 600; font-size: 0.98rem; }

    .pricing-card__pill {
        background: #e7ffe4;
        color: #2f7d33;
        font-size: 0.78rem;
        padding: 4px 10px;
        border-radius: 999px;
        font-weight: 600;
        border: 1px solid rgba(47, 125, 51, 0.2);
    }

    .pricing-card__plan-name { font-weight: 600; font-size: 1rem; margin: 0; }
    .pricing-card__price { font-size: clamp(1.8rem, 3vw, 2.2rem); font-weight: 700; margin: 0; }
    .pricing-card__subtitle { margin: 0; color: #4b5563; font-size: 0.98rem; line-height: 1.5; }

    .pricing-card__list { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 12px; color: #2c333d; font-size: 0.97rem; }
    .pricing-card__list li { position: relative; padding-left: 26px; }
    .pricing-card__list li::before { content: "✓"; position: absolute; left: 0; top: 0; font-weight: 700; }

    .pricing-card__cta {
        margin-top: auto;
        width: 100%;
        padding: 12px 18px;
        border-radius: 999px;
        border: none;
        font-weight: 600;
        font-size: 0.95rem;
        background: rgba(30, 30, 30, 0.08);
        color: #1A1615;
        cursor: pointer;
        font-family: inherit;
        transition: transform 0.2s ease, opacity 0.2s ease;
    }

    .pricing-card__cta:hover {
        transform: translateY(-2px);
        opacity: 0.8;
    }

    .pricing-card__cta--dark { background: #1A1615; color: #ffffff; }
    .pricing-card__cta--dark:hover { transform: translateY(-2px); opacity: 0.85; }

    /* ===== CTA FINAL ===== */
    .cta-final {
        padding: clamp(36px, 6vw, 56px) 0;
        background: transparent;
        position: relative;
        overflow: hidden;
    }

    .cta-final::before {
        content: "";
        position: absolute;
        inset: 0;
        background-image: var(--clouds-image);
        background-repeat: repeat-x;
        background-position: center;
        background-size: 1400px auto;
        opacity: 0.95;
        pointer-events: none;
    }

    .cta-final__inner {
        max-width: 980px;
        margin-inline: auto;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        padding: clamp(20px, 3vw, 32px) 20px;
        position: relative;
        z-index: 1;
    }

    .cta-final__title { margin: 0; font-size: 52px; font-weight: 600; color: #1A1615; line-height: 120%; letter-spacing: -0.03em; }
    .cta-final__subtitle { margin: 0; color: var(--welcome-muted); font-size: 1rem; }

    .cta-final__button {
        margin-top: 8px;
        padding: 12px 26px;
        border-radius: 999px;
        border: none;
        background: #1A1615;
        color: #ffffff;
        font-weight: 600;
        cursor: pointer;
        font-family: inherit;
        transition: transform 0.2s ease, opacity 0.2s ease;
    }

    .cta-final__button:hover { transform: translateY(-2px); opacity: 0.85; }

    /* ===== SCROLL TO TOP ===== */
    .scroll-top {
        position: fixed;
        bottom: 32px;
        right: 32px;
        width: 48px;
        height: 48px;
        border-radius: 999px;
        background: #1A1615;
        color: #ffffff;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 16px rgba(20, 28, 40, 0.2);
        opacity: 0;
        transform: translateY(16px);
        transition: opacity 0.3s ease, transform 0.3s ease;
        z-index: 999;
        pointer-events: none;
    }

    .scroll-top--visible {
        opacity: 1;
        transform: translateY(0);
        pointer-events: auto;
    }

    .scroll-top:hover { opacity: 0.8; transform: translateY(-2px); }

    .scroll-top svg { width: 20px; height: 20px; }

    /* ===== ANIMATIONS ===== */
    .anim-fade-up {
        opacity: 0;
        transform: translateY(36px);
        animation: welcomeFadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        animation-delay: var(--delay, 0ms);
    }

    .anim-fade-down {
        opacity: 0;
        transform: translateY(-20px);
        animation: welcomeFadeDown 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    }

    @keyframes welcomeFadeUp { to { opacity: 1; transform: translateY(0); } }
    @keyframes welcomeFadeDown { to { opacity: 1; transform: translateY(0); } }

    .scroll-reveal {
        opacity: 0;
        transform: translateY(40px);
        transition: opacity 0.75s cubic-bezier(0.22, 1, 0.36, 1),
                    transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);
    }

    .scroll-reveal--visible { opacity: 1; transform: translateY(0); }

    /* ===== RESPONSIVE ===== */

    /* PC portable 1400px */
    @media (max-width: 1440px) {
        .welcome__hero { height: 860px; padding: 80px var(--welcome-inline-pad) 0; }
        .chronic { padding: 80px var(--welcome-inline-pad); padding-top: 180px; }
        .chronic__title { font-size: 40px; }
        .chronic__inner { max-width: 960px; }
        .chronic__image { height: 480px; }

        .solution { padding: 80px var(--welcome-inline-pad); }
        .solution__title { font-size: 40px; margin-bottom: 24px; }
        .solution__inner { max-width: 860px; }
        .solution-carousel__hover {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 20%;
        z-index: 2;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .solution-carousel__hover--left { left: 0; }
    .solution-carousel__hover--right { right: 0; }

    .solution-carousel__hover::after {
        content: '';
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: rgba(255,255,255,0.85);
        backdrop-filter: blur(6px);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.2s ease;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231A1615' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='15 18 9 12 15 6'%3E%3C/polyline%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center;
        background-size: 20px;
    }

    .solution-carousel__hover--right::after {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231A1615' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='9 18 15 12 9 6'%3E%3C/polyline%3E%3C/svg%3E");
    }

    .solution-carousel:hover .solution-carousel__hover::after { opacity: 1; }

    .solution-carousel {
        position: relative;
    }

    .solution-carousel__item { width: 190px; height: 280px; }

        .steps { padding: 80px var(--welcome-inline-pad); }
        .steps__title { font-size: 40px; margin-bottom: 24px; }
        .steps__inner { max-width: 860px; }
        .steps__grid { grid-template-columns: repeat(2, 420px); gap: 16px; }
        .steps-card { width: 420px; height: 360px; }
        .steps-card__image { height: 160px; }
        .steps-card__title { font-size: 22px; }
        .steps-card__text { font-size: 15px; }

        .platforms { padding: 80px var(--welcome-inline-pad); }
        .platforms__title { font-size: 40px; margin-bottom: 24px; }
        .platforms__inner { max-width: 860px; }

        .testimonial { padding: 80px var(--welcome-inline-pad) 96px; }
        .testimonial__title { font-size: 40px; margin-bottom: 24px; }
        .testimonial__inner { max-width: 720px; }
        .testimonial__quote { font-size: 17px; }

        .pricing { padding: 80px var(--welcome-inline-pad); }
        .pricing__title { font-size: 40px; }
        .pricing__inner { max-width: 860px; }
        .pricing-card__price { font-size: 1.6rem; }

        .cta-final { padding: 64px 0; }
        .cta-final__title { font-size: 40px; }
    }

    /* Tablette large */
    @media (max-width: 1024px) {
        .welcome__nav {
            width: calc(100% - 60px);
        }
    }

    /* Tablette */
    @media (max-width: 840px) {
        .navlinks { gap: 16px; }
        .chronic__inner { grid-template-columns: 1fr; }
        .chronic__cta { align-self: center; }
    }

    /* Mobile large / tablette portrait */
    @media (max-width: 768px) {
        .hamburger { display: flex; }
        .navlinks { display: none; }
        .nav-actions { display: none; }

        .welcome__nav {
            background: rgba(240, 238, 235, 0.88);
            backdrop-filter: blur(16px);
            -webkit-backdrop-filter: blur(16px);
            border-radius: 999px;
            padding: 10px 20px;
            border: 1px solid rgba(255, 255, 255, 0.5);
            box-shadow: 0 4px 24px rgba(20, 28, 40, 0.10);
            width: calc(100% - 40px);
        }

        /* 1. Hero plus court, titre + texte adaptatifs */
        .welcome__hero {
            padding: 70px 20px 0px;
            height: 760px;
            min-height: auto;
            overflow: hidden;
        }

        .hero__title { font-size: clamp(1.8rem, 7vw, 2.4rem); margin-top: 56px; }
        .hero__subtitle { font-size: clamp(0.9rem, 3.5vw, 1rem); max-width: 100%; }
        .hero__actions { flex-direction: column; align-items: center; }
        .hero__actions .btn { width: 100%; max-width: 300px; }

        .solution__title-mobile { display: inline; }
        .solution__title-desktop { display: none; }
        .chronic { padding-top: 100px; }

        /* 2b. Solution section font sizes matching steps cards */
        .solution-card__title { font-size: 20px; }
        .solution-card__text { font-size: 14px; }
        .solution-mini h3 { font-size: 20px; margin-bottom: 12px; }
        .solution-mini p { font-size: 14px; }

        /* 3. Titres de sections réduits */
        .chronic__title { font-size: 36px; }
        .solution__title { font-size: 34px; }
        .steps__title { font-size: 34px; }
        .platforms__title { font-size: 34px; }
        .testimonial__title { font-size: 34px; }
        .pricing__title { font-size: 34px; }
        .cta-final__title { font-size: 34px; }

        /* 4. Carousel images - rectangle, moins d'espace */
        .solution-carousel__hover {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 20%;
        z-index: 2;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .solution-carousel__hover--left { left: 0; }
    .solution-carousel__hover--right { right: 0; }

    .solution-carousel__hover::after {
        content: '';
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: rgba(255,255,255,0.85);
        backdrop-filter: blur(6px);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.2s ease;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231A1615' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='15 18 9 12 15 6'%3E%3C/polyline%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center;
        background-size: 20px;
    }

    .solution-carousel__hover--right::after {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231A1615' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='9 18 15 12 9 6'%3E%3C/polyline%3E%3C/svg%3E");
    }

    .solution-carousel:hover .solution-carousel__hover::after { opacity: 1; }

    .solution-carousel {
        position: relative;
    }

    .solution-carousel__item { width: 160px; height: 220px; }
        .solution-carousel__track { gap: 14px; }

        /* 5. Solution blocs espacés uniformément */
        .solution__bottom { gap: 14px; margin-top: 14px; }
        .solution-mini { padding: 16px; }

        /* 6. Steps grid 1 colonne */
        .steps__grid { grid-template-columns: 1fr; }
        .steps-card { width: 100%; min-height: auto; height: auto; }
        .steps-card__title { font-size: 20px; }
        .steps-card__text { font-size: 15px; }
        .steps-card__image { height: 180px; }

        /* 7. Testimonial quote plus petit */
        .testimonial__quote { font-size: 0.95rem; }

        .pricing__grid { grid-template-columns: 1fr; }
        .pricing-card { max-width: 100%; align-items: stretch; }

        .platforms__switch { bottom: 16px; }
        .platforms__card { border-radius: 22px; }
    }

    /* Mobile */
    @media (max-width: 480px) {
        :root { --welcome-inline-pad: 16px; }

        .welcome__nav { width: calc(100% - 32px); }

        .hero__title { font-size: clamp(1.6rem, 7vw, 2rem); }
        .hero__subtitle { font-size: 0.88rem; }

        .solution-card { padding: 20px 16px; }
        .solution-carousel__hover {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 20%;
        z-index: 2;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .solution-carousel__hover--left { left: 0; }
    .solution-carousel__hover--right { right: 0; }

    .solution-carousel__hover::after {
        content: '';
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: rgba(255,255,255,0.85);
        backdrop-filter: blur(6px);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.2s ease;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231A1615' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='15 18 9 12 15 6'%3E%3C/polyline%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center;
        background-size: 20px;
    }

    .solution-carousel__hover--right::after {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231A1615' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='9 18 15 12 9 6'%3E%3C/polyline%3E%3C/svg%3E");
    }

    .solution-carousel:hover .solution-carousel__hover::after { opacity: 1; }

    .solution-carousel {
        position: relative;
    }

    .solution-carousel__item { width: 140px; height: 200px; }
        .solution-carousel__track { gap: 10px; }

        .chronic__title { font-size: 30px; }
        .solution__title, .steps__title, .platforms__title,
        .testimonial__title, .pricing__title, .cta-final__title { font-size: 28px; }

        .steps-card__title { font-size: 18px; }
        .steps-card__text { font-size: 14px; }
        .steps-card__image { height: 160px; }
    }
</style>
